import { motion } from 'framer-motion'
import { useProjects } from '../hooks/useProjects'
import { GradientBackground } from './GradientBackground'

export function ProjectSkatepark() {
  const { getRandomProject } = useProjects()
  
  const handleNextProject = () => {
    const randomProject = getRandomProject('genova-pra-skatepark')
    if (randomProject) {
      window.location.hash = randomProject.slug
    }
  }
  
  return (
    <GradientBackground>
      {/* Hero Section - Video gameplay */}
      <section className="relative h-[65vh] w-full">
        <video 
          src="/assets/projects/genova-pra-skatepark/skatepark_pra.mp4"
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-full object-cover object-center"
          poster="/assets/projects/genova-pra-skatepark/Screenshot-2024-10-23-175131.png"
        />
      </section>

      {/* Project Info */}
      <section className="py-20 px-4" style={{ background: 'linear-gradient(to bottom, transparent 0%, #CFCFCF 100%)' }}>
        <div className="container mx-auto max-w-4xl">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16"
          >
            <div>
              <h3 className="text-sm uppercase tracking-wider text-white/60 mb-2">Client</h3>
              <p className="text-lg font-medium text-white">Personal Project</p>
            </div>
            <div>
              <h3 className="text-sm uppercase tracking-wider text-white/60 mb-2">Services</h3>
              <p className="text-lg font-medium text-white">3D Modeling, Game Mod</p>
            </div>
            <div>
              <h3 className="text-sm uppercase tracking-wider text-white/60 mb-2">Year</h3>
              <p className="text-lg font-medium text-white">2024</p>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="prose prose-lg max-w-none"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">The Project</h2>
            <p className="text-xl text-white/80 leading-relaxed mb-12">
              Geometric precision, asset optimization, and architectural fidelity: 
              I faithfully recreated the Genova Prà skatepark as a mod for Session: Skate Sim. 
              Prà's park is not only a reference point for the Ligurian skate scene, but is a 
              certified structure compliant with standards to host national and international competitions. 
              A project that explores the translation of physical design into interactive digital experience.
            </p>

            {/* Images Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
              <div className="space-y-8">
                <img 
                  src="/assets/projects/genova-pra-skatepark/Screenshot-2024-10-23-175131.png" 
                  alt="Skatepark View 1"
                  className="w-full rounded-lg shadow-lg"
                />
                <img 
                  src="/assets/projects/genova-pra-skatepark/Screenshot-2024-10-26-134639.png" 
                  alt="Skatepark View 2"
                  className="w-full rounded-lg shadow-lg"
                />
                <img 
                  src="/assets/projects/genova-pra-skatepark/Screenshot-2024-11-01-133222.png" 
                  alt="Skatepark View 3"
                  className="w-full rounded-lg shadow-lg"
                />
                <img 
                  src="/assets/projects/genova-pra-skatepark/Screenshot-2024-11-03-011336.png" 
                  alt="Skatepark View 4"
                  className="w-full rounded-lg shadow-lg"
                />
              </div>
              <div className="space-y-8">
                <img 
                  src="/assets/projects/genova-pra-skatepark/Screenshot-2024-10-23-175145.png" 
                  alt="Skatepark View 5"
                  className="w-full rounded-lg shadow-lg"
                />
                <img 
                  src="/assets/projects/genova-pra-skatepark/Screenshot-2024-10-26-214523.png" 
                  alt="Skatepark View 6"
                  className="w-full rounded-lg shadow-lg"
                />
                <img 
                  src="/assets/projects/genova-pra-skatepark/Screenshot-2024-11-01-161707.png" 
                  alt="Skatepark View 7"
                  className="w-full rounded-lg shadow-lg"
                />
                <img 
                  src="/assets/projects/genova-pra-skatepark/Screenshot-2024-11-03-011444.png" 
                  alt="Skatepark View 8"
                  className="w-full rounded-lg shadow-lg"
                />
              </div>
            </div>

            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">Technical Details</h2>
            <p className="text-xl text-white/80 leading-relaxed mb-12">
              The 3D reconstruction of the skatepark required careful study of proportions 
              and geometries. Every element was modeled to ensure maximum fidelity to the original, 
              allowing virtual skaters to experience the park exactly as in reality.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Tools Section */}
      <section className="py-16 px-4 bg-[#CFCFCF]">
        <div className="container mx-auto max-w-4xl">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h3 className="text-sm uppercase tracking-wider text-white/60 mb-4">Tools Used</h3>
            <div className="flex flex-wrap justify-center gap-4">
              {['Blender', 'Session: Skate Sim'].map((tool) => (
                <span 
                  key={tool}
                  className="px-6 py-3 bg-white text-white font-medium rounded-lg shadow-sm"
                >
                  {tool}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Related Projects */}
      <section className="py-20 px-4" style={{ background: 'linear-gradient(to bottom, transparent 0%, #CFCFCF 100%)' }}>
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/#works"
              className="group relative inline-flex items-center justify-center gap-2 px-8 py-4 text-white text-lg font-medium rounded-lg overflow-hidden cursor-pointer transition-transform duration-300 hover:scale-105" style={{ background: 'linear-gradient(90deg, #7ec876 0%, #4da6ff 50%, #7ec876 100%)', backgroundSize: '200% 100%', animation: 'gradient-shift 3s ease infinite' }}
            >
              ← Back to Works
            </a>
            <button 
              onClick={handleNextProject}
              className="group relative inline-flex items-center justify-center gap-2 px-8 py-4 text-white text-lg font-medium rounded-lg overflow-hidden cursor-pointer transition-transform duration-300 hover:scale-105" style={{ background: 'linear-gradient(90deg, #7ec876 0%, #4da6ff 50%, #7ec876 100%)', backgroundSize: '200% 100%', animation: 'gradient-shift 3s ease infinite' }}
            >
              Next Project →
            </button>
          </div>
        </div>
      </section>

    </GradientBackground>
  )
}